import { Avatar as AvatarPrimitive } from 'radix-ui';
import styled from 'styled-components';

import { UI_SHAPE } from '@savgroup-front-common/constants/shared/shape';
import { UI_SIZE } from '@savgroup-front-common/constants/shared/size';
import { lighten } from '@savgroup-front-common/primitive/helpers/cssColor';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { rem } from '@savgroup-front-common/primitive/helpers/units';

import { fontStyle } from '../../theme/helpers/fontStyle';

import { AVATAR_SIZE, AVATAR_VARIANT } from './Avatar.types';

const AVATAR_XXS_FONT_SIZE = rem(8);

export const $AvatarRoot = styled(AvatarPrimitive.Root)`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
  user-select: none;
  width: ${spacing('xxl')};
  height: ${spacing('xxl')};

  &[data-shape='${UI_SHAPE.SQUARE}'] {
    border-radius: ${({ theme }) => theme.tw.radius.sm};
  }

  &[data-size='${AVATAR_SIZE.XXS}'] {
    width: ${spacing(5)};
    height: ${spacing(5)};
  }

  &[data-size='${UI_SIZE.XS}'] {
    width: ${spacing('xl')};
    height: ${spacing('xl')};
  }

  &[data-size='${UI_SIZE.SM}'] {
    width: ${spacing(7)};
    height: ${spacing(7)};
  }

  &[data-size='${UI_SIZE.LG}'] {
    width: ${spacing(12)};
    height: ${spacing(12)};
  }

  &[data-size='${UI_SIZE.XL}'] {
    width: ${spacing(14)};
    height: ${spacing(14)};
  }
`;

export const $AvatarImage = styled(AvatarPrimitive.Image)`
  aspect-ratio: 1;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const $AvatarFallback = styled(AvatarPrimitive.Fallback)`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  background-color: ${({ theme }) => theme.colors.disabled};
  color: ${({ theme }) => theme.colors.black};
  ${fontStyle('sm')};
  font-weight: ${fontWeight('medium')};

  [data-size='${AVATAR_SIZE.XXS}'] > & {
    font-size: ${AVATAR_XXS_FONT_SIZE};
    line-height: 1;
  }

  [data-size='${UI_SIZE.XS}'] > & {
    ${fontStyle('xxs')};
  }

  [data-size='${UI_SIZE.SM}'] > & {
    ${fontStyle('xs')};
  }

  [data-size='${UI_SIZE.LG}'] > & {
    ${fontStyle('base')};
  }

  [data-size='${UI_SIZE.XL}'] > & {
    ${fontStyle('lg')};
  }

  [data-variant='${AVATAR_VARIANT.SLATE}'] > & {
    background-color: #f1f5f9;
    color: #1e293b;
  }

  [data-variant='${AVATAR_VARIANT.GRAY}'] > & {
    background-color: #f3f4f6;
    color: #1f2937;
  }

  [data-variant='${AVATAR_VARIANT.ZINC}'] > & {
    background-color: #f4f4f5;
    color: #27272a;
  }

  [data-variant='${AVATAR_VARIANT.RED}'] > & {
    background-color: #fee2e2;
    color: #991b1b;
  }

  [data-variant='${AVATAR_VARIANT.YELLOW}'] > & {
    background-color: #fef3c7;
    color: #854d0e;
  }

  [data-variant='${AVATAR_VARIANT.GREEN}'] > & {
    background-color: #d1fae5;
    color: #166534;
  }

  [data-variant='${AVATAR_VARIANT.ORANGE}'] > & {
    background-color: #ffedd5;
    color: #9a3412;
  }

  [data-variant='${AVATAR_VARIANT.BLUE}'] > & {
    background-color: #dbeafe;
    color: #1e40af;
  }

  [data-variant='${AVATAR_VARIANT.PINK}'] > & {
    background-color: #fce7f3;
    color: #9d174d;
  }

  [data-variant='${AVATAR_VARIANT.AMBER}'] > & {
    background-color: #fef3c7;
    color: #92400e;
  }

  [data-variant='${AVATAR_VARIANT.LIME}'] > & {
    background-color: #ecfccb;
    color: #3f6212;
  }

  [data-variant='${AVATAR_VARIANT.EMERALD}'] > & {
    background-color: #d1fae5;
    color: #065f46;
  }

  [data-variant='${AVATAR_VARIANT.TEAL}'] > & {
    background-color: #ccfbf1;
    color: #115e59;
  }

  [data-variant='${AVATAR_VARIANT.CYAN}'] > & {
    background-color: #cffafe;
    color: #155e75;
  }

  [data-variant='${AVATAR_VARIANT.SKY}'] > & {
    background-color: #e0f2fe;
    color: #075985;
  }

  [data-variant='${AVATAR_VARIANT.INDIGO}'] > & {
    background-color: #e0e7ff;
    color: #3730a3;
  }

  [data-variant='${AVATAR_VARIANT.VIOLET}'] > & {
    background-color: #ede9fe;
    color: #5b21b6;
  }

  [data-variant='${AVATAR_VARIANT.PURPLE}'] > & {
    background-color: #f3e8ff;
    color: #6b21a8;
  }

  [data-variant='${AVATAR_VARIANT.FUCHSIA}'] > & {
    background-color: #fae8ff;
    color: #86198f;
  }

  [data-variant='${AVATAR_VARIANT.ROSE}'] > & {
    background-color: #ffe4e6;
    color: #9f1239;
  }

  // is-header override all variant
  [data-is-header='true'] > & {
    background-color: ${({ theme }) => theme.newUI.variationColors?.light5};
    color: ${({ theme }) => theme.newUI.defaultColors.heading};

    &:hover {
      background-color: ${({ theme }) =>
        lighten(0.05, theme.newUI.variationColors?.light5)};
      color: ${({ theme }) => theme.colors.headingTextColor};
    }
  }
`;

export const $AvatarBadge = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  user-select: none;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.white};
  width: ${spacing(2.5)};
  height: ${spacing(2.5)};

  & > svg {
    width: ${spacing('sm')};
    height: ${spacing('sm')};
  }

  [data-size='${AVATAR_SIZE.XXS}'] & {
    width: ${spacing(1.5)};
    height: ${spacing(1.5)};

    & > svg {
      display: none;
    }
  }

  [data-size='${UI_SIZE.XS}'] & {
    width: ${spacing('sm')};
    height: ${spacing('sm')};

    & > svg {
      display: none;
    }
  }

  [data-size='${UI_SIZE.SM}'] & {
    width: ${spacing('sm')};
    height: ${spacing('sm')};

    & > svg {
      display: none;
    }
  }

  [data-size='${UI_SIZE.LG}'] & {
    width: ${spacing('md')};
    height: ${spacing('md')};

    & > svg {
      width: ${spacing('sm')};
      height: ${spacing('sm')};
    }
  }

  [data-size='${UI_SIZE.XL}'] & {
    width: ${spacing(3.5)};
    height: ${spacing(3.5)};

    & > svg {
      width: ${spacing(2.5)};
      height: ${spacing(2.5)};
    }
  }
`;

export const $AvatarGroup = styled.div`
  display: flex;

  & > [data-slot='avatar'] + [data-slot='avatar'] {
    margin-left: -${spacing('sm')};
  }

  & > [data-slot='avatar'] {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.white};
  }
`;

export const $AvatarGroupCount = styled.div`
  position: relative;
  display: flex;
  width: ${spacing('xxl')};
  height: ${spacing('xxl')};
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.disabled};
  color: ${({ theme }) => theme.colors.tertiary};
  ${fontStyle('sm')};
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.white};

  & > svg {
    width: ${spacing('lg')};
    height: ${spacing('lg')};
  }

  [data-slot='avatar-group']:has([data-size='${AVATAR_SIZE.XXS}']) > & {
    width: ${spacing('lg')};
    height: ${spacing('lg')};

    & > svg {
      width: ${spacing('sm')};
      height: ${spacing('sm')};
    }
  }

  [data-slot='avatar-group']:has([data-size='${UI_SIZE.XS}']) > & {
    width: ${spacing(5)};
    height: ${spacing(5)};

    & > svg {
      width: ${spacing(2.5)};
      height: ${spacing(2.5)};
    }
  }

  [data-slot='avatar-group']:has([data-size='${UI_SIZE.SM}']) > & {
    width: ${spacing('xl')};
    height: ${spacing('xl')};

    & > svg {
      width: ${spacing('md')};
      height: ${spacing('md')};
    }
  }

  [data-slot='avatar-group']:has([data-size='${UI_SIZE.LG}']) > & {
    width: ${spacing(10)};
    height: ${spacing(10)};

    & > svg {
      width: ${spacing(5)};
      height: ${spacing(5)};
    }
  }

  [data-slot='avatar-group']:has([data-size='${UI_SIZE.XL}']) > & {
    width: ${spacing(11)};
    height: ${spacing(11)};

    & > svg {
      width: ${spacing(5)};
      height: ${spacing(5)};
    }
  }
`;
